/**
 * useCurrencyInput
 * 
 * Hook para gerenciar inputs de moeda (BRL).
 * Fornece funções para formatar, parsear e manipular eventos de change.
 */
import { useState, useCallback, ChangeEvent } from 'react';

/**
 * Formata um número para string de moeda brasileira (sem R$)
 * Ex: 1250.5 -> "1.250,50"
 */
export const formatCurrency = (val: number): string => {
    return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Converte string formatada (1.250,50) para número float (1250.50)
 */
export const parseCurrency = (val: string): number => {
    // Remove tudo que não é dígito ou vírgula
    const cleaned = val.replace(/[^\d,]/g, '');
    // Troca vírgula por ponto
    const normalized = cleaned.replace(',', '.');
    // Remove pontos de milhar se houver (ex: 1.000,00 -> 1000.00) se a lógica acima falhar
    // A regex /[^\d,]/g remove pontos, então "1.250,50" vira "1250,50" -> "1250.50"
    return parseFloat(normalized) || 0;
};

/**
 * Aplica máscara de moeda enquanto digita
 * Ex: "1234" -> "12,34"
 */
export const maskCurrency = (value: string): string => {
    // Remove tudo que não é dígito
    let numbers = value.replace(/\D/g, '');

    // Se estiver vazio
    if (!numbers) return '';

    // Converte para decimal (dividir por 100)
    const amount = parseInt(numbers) / 100;

    // Formata
    return formatCurrency(amount);
};

export const useCurrencyInput = (initialValue: number = 0) => {
    const [displayValue, setDisplayValue] = useState<string>(formatCurrency(initialValue));
    const [rawValue, setRawValue] = useState<number>(initialValue);

    // Atualiza ambos valores quando o numérico muda externamente
    const setValue = useCallback((val: number) => {
        setRawValue(val);
        setDisplayValue(formatCurrency(val));
    }, []);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | string) => {
        const inputValue = typeof e === 'string' ? e : e.target.value;

        // Aplica a máscara e atualiza display
        // A lógica do maskCurrency já lida com a formatação correta pt-BR
        const masked = maskCurrency(inputValue);
        setDisplayValue(masked);

        // Atualiza o valor bruto
        const numeric = parseCurrency(masked);
        setRawValue(numeric);
    }, []);

    return {
        displayValue,
        rawValue,
        setValue,
        handleChange,
        formatCurrency,
        parseCurrency
    };
};
